import { z } from "zod";

import { type Op, type Ref } from "./protocol.ts";

// Shape types that own a `.textFrame`. Per the Office.js docs, accessing
// `.textFrame` on other types (Picture, Line, Table, Connector, ...) throws
// InvalidArgument. We gate by type so the typed ops never touch a missing
// text frame.
const TEXT_BEARING_TYPES: ReadonlySet<string> = new Set([
  "Placeholder",
  "TextBox",
  "GeometricShape",
  "Callout",
]);

const isRef = (v: unknown): v is Ref => {
  if (typeof v !== "object" || v === null) return false;
  if (!("ref" in v)) return false;
  return typeof v.ref === "number";
};

// Walk a result tree without `as` — uses Object.entries so unknown stays unknown.
const resolveRef = (ref: Ref, results: readonly unknown[]): unknown => {
  let cur: unknown = results[ref.ref];
  for (const segment of ref.path ?? []) {
    if (cur === null || typeof cur !== "object") return undefined;
    let next: unknown = undefined;
    let found = false;
    for (const [k, v] of Object.entries(cur)) {
      const matches = typeof segment === "number" ? k === String(segment) : k === segment;
      if (matches) {
        next = v;
        found = true;
        break;
      }
    }
    if (!found) return undefined;
    cur = next;
  }
  return cur;
};

const slideIndexSchema = z.number().int().nonnegative();

// Defence in depth: even though the forwarder roots at context.presentation
// (no Office.context reachable from there), block any method whose name
// matches a known egress / auth surface in case Office.js adds one later.
const FORBIDDEN_METHODS = new Set<string>([
  "getAccessTokenAsync",
  "displayDialogAsync",
  "openBrowserWindow",
  "messageParent",
]);

type Segment = string | { method: string; args: readonly unknown[] };

const isMethodSegment = (s: Segment): s is { method: string; args: readonly unknown[] } =>
  typeof s !== "string";

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

// Recursively resolve any Refs embedded inside a value (e.g. method args or
// the value of an oj.set op). Plain objects and arrays are walked; primitives
// pass through unchanged. This lets the model thread one op's result into the
// next op's args inside a single pp.batch — the whole point of batching.
const resolveDeep = (v: unknown, results: readonly unknown[]): unknown => {
  if (isRef(v)) return resolveRef(v, results);
  if (Array.isArray(v)) return v.map((item) => resolveDeep(item, results));
  if (isRecord(v)) {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) out[k] = resolveDeep(val, results);
    return out;
  }
  return v;
};

// Walk a path of segments starting at `root`. Methods are invoked, props
// accessed. Throws on forbidden methods or undefined intermediate values.
// Refs anywhere in method args are resolved against the batch's prior results.
const walkPath = (
  root: unknown,
  path: readonly Segment[],
  prev: readonly unknown[],
): unknown => {
  let cur: unknown = root;
  for (const seg of path) {
    if (cur === null || cur === undefined) {
      throw new Error(`Path step on ${cur === null ? "null" : "undefined"} value`);
    }
    if (isMethodSegment(seg)) {
      if (FORBIDDEN_METHODS.has(seg.method)) {
        throw new Error(`Method ${seg.method} is not allowed via the forwarder`);
      }
      if (!isRecord(cur)) {
        throw new Error(`Cannot call .${seg.method}() on non-object`);
      }
      const fn = cur[seg.method];
      if (typeof fn !== "function") {
        throw new Error(`No method ${seg.method} on path target`);
      }
      const resolvedArgs = seg.args.map((a) => resolveDeep(a, prev));
      cur = fn.apply(cur, resolvedArgs);
    } else {
      if (!isRecord(cur)) {
        throw new Error(`Cannot access .${seg} on non-object`);
      }
      cur = cur[seg];
    }
  }
  return cur;
};

// Heuristic: a ClientResult has a single `.value` getter and no `.load`.
const isClientResult = (v: unknown): v is { value: unknown } => {
  if (!isRecord(v)) return false;
  return "value" in v && typeof v.value !== "function" && !("load" in v);
};

const hasLoad = (v: unknown): v is { load: (props?: string) => void } => {
  if (!isRecord(v)) return false;
  return typeof v.load === "function";
};

const hasToJSON = (v: unknown): v is { toJSON: () => unknown } => {
  if (!isRecord(v)) return false;
  return typeof v.toJSON === "function";
};

const resolveSlideIndex = (slide: number | Ref, results: readonly unknown[]): number => {
  const raw = isRef(slide) ? resolveRef(slide, results) : slide;
  const parsed = slideIndexSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Could not resolve slide index from ${JSON.stringify(slide)} → ${JSON.stringify(raw)}`);
  }
  return parsed.data;
};

// Runs the ops inside one PowerPoint.run. Each op queues work on the shared
// context; we sync only when an op actually needs a result, plus once at
// the end to flush queued writes. On error we annotate with the failing
// op index so the model can recover.
export const executeOps = async (ops: readonly Op[]): Promise<unknown[]> => {
  const results: unknown[] = [];
  await PowerPoint.run(async (context) => {
    for (let i = 0; i < ops.length; i++) {
      const op = ops[i];
      if (!op) continue;
      try {
        const result = await executeOp(op, context, results);
        results.push(result);
      } catch (e) {
        const original = e instanceof Error ? e.message : String(e);
        const wrapped = new Error(`Op ${i.toString()} (${op.op}) failed: ${original}`);
        if (e instanceof Error && e.stack) wrapped.stack = e.stack;
        throw wrapped;
      }
    }
    // Final flush: many ops (oj.set, void oj.call) queue work without
    // syncing. Make sure any pending writes are committed before the run
    // ends, so errors surface here rather than from PowerPoint.run's
    // implicit sync (where the per-op annotation is already gone).
    try {
      await context.sync();
    } catch (e) {
      const original = e instanceof Error ? e.message : String(e);
      const lastIdx = Math.max(0, ops.length - 1);
      const lastOp = ops[lastIdx]?.op ?? "unknown";
      throw new Error(
        `Op ${lastIdx.toString()} (${lastOp}) failed: Final sync — ${original}. Earlier ops in this batch may already be committed; re-read the slide before retrying.`,
      );
    }
  });
  return results;
};

const executeOp = async (
  op: Op,
  context: PowerPoint.RequestContext,
  prev: readonly unknown[],
): Promise<unknown> => {
  switch (op.op) {
    case "getOutline": {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const outline = slides.items.map((_slide, index) => ({ index }));
      return { outline };
    }
    case "addSlide": {
      context.presentation.slides.add();
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      return { index: slides.items.length - 1 };
    }
    case "getSelection": {
      // "The current selection is not supported." comes back when the user
      // has a shape selected without entering its text — common case, not an
      // error. Surface it as text: null so the model can branch on it.
      const result = await new Promise<{ text: string | null; unsupported?: boolean }>((resolve) => {
        Office.context.document.getSelectedDataAsync(Office.CoercionType.Text, (r) => {
          if (r.status !== Office.AsyncResultStatus.Succeeded) {
            resolve({ text: null, unsupported: true });
            return;
          }
          resolve({ text: typeof r.value === "string" ? r.value : "" });
        });
      });
      return result;
    }
    case "setFirstShapeText": {
      const idx = resolveSlideIndex(op.slide, prev);
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const slide = slides.items[idx];
      if (!slide) throw new Error(`No slide at index ${idx}`);
      const shapes = slide.shapes;
      shapes.load("items/id,items/type");
      await context.sync();
      const canPlaceholder =
        typeof Office !== "undefined" &&
        !!Office.context?.requirements?.isSetSupported("PowerPointApi", "1.8");
      let shape: PowerPoint.Shape | undefined;
      if (canPlaceholder) {
        const placeholders = shapes.items.filter((s) => s.type === "Placeholder");
        for (const s of placeholders) s.load("placeholderFormat/type");
        if (placeholders.length > 0) await context.sync();
        shape = placeholders.find((s) => {
          try {
            const t = s.placeholderFormat.type;
            return t === "Title" || t === "CenterTitle";
          } catch {
            return false;
          }
        });
      }
      if (!shape) {
        shape = shapes.items.find((s) => TEXT_BEARING_TYPES.has(s.type));
      }
      if (!shape) {
        throw new Error(
          `Slide ${idx} has no text-bearing shape to write into (no Title placeholder or text box).`,
        );
      }
      shape.textFrame.textRange.text = op.text;
      await context.sync();
      return { slideIndex: idx };
    }
    case "countSlides": {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      return { count: slides.items.length };
    }
    case "insertSlidesFromBase64": {
      // With an empty options object, Office.js inserts at the beginning of
      // the deck (not the end). Force insertion after the last existing
      // slide so the returned firstIndex matches where slides actually land.
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const before = slides.items.length;
      const lastSlide = before > 0 ? slides.items[before - 1] : null;
      const options: PowerPoint.InsertSlideOptions = {};
      if (lastSlide) options.targetSlideId = lastSlide.id;
      if (op.sourceSlideIds && op.sourceSlideIds.length > 0) {
        options.sourceSlideIds = op.sourceSlideIds;
      }
      context.presentation.insertSlidesFromBase64(op.base64, options);
      slides.load("items/id");
      await context.sync();
      const after = slides.items.length;
      const insertedIds: string[] = [];
      for (let i = before; i < after; i++) {
        const s = slides.items[i];
        if (s) insertedIds.push(s.id);
      }
      return {
        firstIndex: before,
        count: Math.max(0, after - before),
        slideIds: insertedIds,
      };
    }
    case "replaceDeckWithBase64": {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const originalCount = slides.items.length;
      const originals: PowerPoint.Slide[] = [];
      for (let i = 0; i < originalCount; i++) {
        const s = slides.items[i];
        if (s) originals.push(s);
      }
      context.presentation.insertSlidesFromBase64(op.base64, {});
      slides.load("items/id");
      await context.sync();
      const afterInsert = slides.items.length;
      const inserted = Math.max(0, afterInsert - originalCount);
      if (inserted === 0) {
        throw new Error(
          "Snapshot contained no slides — refusing to delete the live deck",
        );
      }
      for (const s of originals) s.delete();
      await context.sync();
      return { restored: inserted };
    }
    case "replaceSlideFromBase64": {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const currentSlide = slides.items[op.currentSlideIndex];
      if (!currentSlide) {
        throw new Error(`No slide at index ${op.currentSlideIndex.toString()}`);
      }
      const predSlide = op.currentSlideIndex > 0
        ? slides.items[op.currentSlideIndex - 1] ?? null
        : null;
      const options: PowerPoint.InsertSlideOptions = {
        sourceSlideIds: [op.sourceSlideId],
      };
      if (predSlide) options.targetSlideId = predSlide.id;
      context.presentation.insertSlidesFromBase64(op.base64, options);
      currentSlide.delete();
      await context.sync();
      return { replacedIndex: op.currentSlideIndex };
    }
    case "tagScratchSlides": {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const total = slides.items.length;
      const marker = `[${op.scratchTag}] `;

      // Phase 1: bulk-load first-shape type for every target slide.
      const targets = [];
      for (let i = op.firstIndex; i < op.firstIndex + op.count && i < total; i++) {
        const slide = slides.items[i];
        if (!slide) continue;
        slide.shapes.load("items/type");
        targets.push({ idx: i, slide });
      }
      if (targets.length > 0) await context.sync();

      // Phase 2: only on shapes whose type is text-bearing, queue a text load.
      const taggable: { titleShape: PowerPoint.Shape }[] = [];
      for (const t of targets) {
        const titleShape = t.slide.shapes.items[0];
        if (!titleShape) continue;
        if (!TEXT_BEARING_TYPES.has(titleShape.type)) continue;
        titleShape.textFrame.textRange.load("text");
        taggable.push({ titleShape });
      }
      if (taggable.length > 0) await context.sync();

      // Phase 3: write prefix in a single batch.
      let tagged = 0;
      for (const { titleShape } of taggable) {
        const current = titleShape.textFrame.textRange.text ?? "";
        if (!current.startsWith(marker)) {
          titleShape.textFrame.textRange.text = `${marker}${current}`;
          tagged += 1;
        }
      }
      if (tagged > 0) await context.sync();
      return { tagged };
    }
    case "listScratchSlides": {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const marker = `[${op.scratchTag}]`;

      const all: { idx: number; slide: PowerPoint.Slide }[] = [];
      for (let i = 0; i < slides.items.length; i++) {
        const slide = slides.items[i];
        if (!slide) continue;
        slide.shapes.load("items/type");
        all.push({ idx: i, slide });
      }
      if (all.length > 0) await context.sync();

      const probes: { idx: number; titleShape: PowerPoint.Shape }[] = [];
      for (const a of all) {
        const titleShape = a.slide.shapes.items[0];
        if (!titleShape) continue;
        if (!TEXT_BEARING_TYPES.has(titleShape.type)) continue;
        titleShape.textFrame.textRange.load("text");
        probes.push({ idx: a.idx, titleShape });
      }
      if (probes.length > 0) await context.sync();

      const matches: number[] = [];
      for (const p of probes) {
        const text = p.titleShape.textFrame.textRange.text ?? "";
        if (text.startsWith(marker)) matches.push(p.idx);
      }
      return { indices: matches };
    }
    case "getSlideContents": {
      const idx = resolveSlideIndex(op.slide, prev);
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const slide = slides.items[idx];
      if (!slide) throw new Error(`No slide at index ${idx}`);

      // Phase 1: load shape identity + type ONLY. Loading placeholderFormat
      // or textFrame paths in bulk over a mixed shape collection throws (and
      // on Mac has been linked to host instability). Probe type first, then
      // gate each subsequent load on the type.
      const shapes = slide.shapes;
      shapes.load("items/id,items/name,items/type,items/left,items/top,items/width,items/height");
      await context.sync();

      // Phase 2: for each shape, queue only the loads that are safe given
      // its type. placeholderFormat requires PowerPointApi 1.8.
      const canPlaceholder =
        typeof Office !== "undefined" &&
        !!Office.context?.requirements?.isSetSupported("PowerPointApi", "1.8");
      const TEXT_BEARING: ReadonlySet<string> = new Set([
        "Placeholder",
        "TextBox",
        "GeometricShape",
        "Callout",
      ]);
      const placeholderShapes: PowerPoint.Shape[] = [];
      const textShapes: PowerPoint.Shape[] = [];
      for (const sh of shapes.items) {
        if (canPlaceholder && sh.type === "Placeholder") {
          sh.load("placeholderFormat/type");
          placeholderShapes.push(sh);
        }
        if (TEXT_BEARING.has(sh.type)) {
          sh.load("textFrame/hasText");
          textShapes.push(sh);
        }
      }
      if (placeholderShapes.length > 0 || textShapes.length > 0) {
        await context.sync();
      }

      // Phase 3: load text only on shapes whose text frame actually has text.
      const shapesWithText: PowerPoint.Shape[] = [];
      for (const sh of textShapes) {
        if (sh.textFrame.hasText) {
          sh.textFrame.textRange.load("text");
          shapesWithText.push(sh);
        }
      }
      if (shapesWithText.length > 0) await context.sync();

      const placeholderIds = new Set(placeholderShapes.map((s) => s.id));
      const textfulIds = new Set(shapesWithText.map((s) => s.id));

      const out = shapes.items.map((sh) => {
        let placeholderType: string | null = null;
        if (placeholderIds.has(sh.id)) {
          try {
            placeholderType = sh.placeholderFormat.type;
          } catch {
            placeholderType = null;
          }
        }
        const hasText = TEXT_BEARING.has(sh.type) && sh.textFrame.hasText;
        const text = textfulIds.has(sh.id) ? sh.textFrame.textRange.text : "";
        return {
          id: sh.id,
          name: sh.name,
          type: sh.type,
          left: sh.left,
          top: sh.top,
          width: sh.width,
          height: sh.height,
          hasText,
          text,
          placeholderType,
        };
      });
      return { slideIndex: idx, shapes: out };
    }
    case "addShapes": {
      const idx = resolveSlideIndex(op.slide, prev);
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const slide = slides.items[idx];
      if (!slide) throw new Error(`No slide at index ${idx}`);

      // Chunk size keeps each sync's host workload modest. 10 shapes ≈
      // 30–60 queued operations per sync, which Mac PowerPoint handles
      // reliably; larger chunks have shown stability issues.
      const SHAPE_CHUNK = 10;
      const ids: string[] = [];

      for (let i = 0; i < op.shapes.length; i += SHAPE_CHUNK) {
        const chunk = op.shapes.slice(i, i + SHAPE_CHUNK);
        const proxies: PowerPoint.Shape[] = [];

        for (const spec of chunk) {
          let shape: PowerPoint.Shape;
          if (spec.kind === "geometric") {
            shape = slide.shapes.addGeometricShape(
              spec.geo as PowerPoint.GeometricShapeType,
              spec.rect,
            );
            if (spec.fill !== undefined) {
              shape.fill.setSolidColor(spec.fill);
            }
            if (spec.line) {
              if (spec.line.color !== undefined) shape.lineFormat.color = spec.line.color;
              if (spec.line.weight !== undefined) shape.lineFormat.weight = spec.line.weight;
              if (spec.line.transparency !== undefined) {
                shape.lineFormat.transparency = spec.line.transparency;
              }
            }
          } else {
            shape = slide.shapes.addTextBox(spec.text, spec.rect);
            if (spec.font) {
              const f = shape.textFrame.textRange.font;
              if (spec.font.name !== undefined) f.name = spec.font.name;
              if (spec.font.size !== undefined) f.size = spec.font.size;
              if (spec.font.color !== undefined) f.color = spec.font.color;
              if (spec.font.bold !== undefined) f.bold = spec.font.bold;
              if (spec.font.italic !== undefined) f.italic = spec.font.italic;
            }
            if (spec.paragraph?.alignment !== undefined) {
              shape.textFrame.textRange.paragraphFormat.horizontalAlignment =
                spec.paragraph.alignment as PowerPoint.ParagraphHorizontalAlignment;
            }
            if (spec.textFrame) {
              const tf = shape.textFrame;
              if (spec.textFrame.wordWrap !== undefined) tf.wordWrap = spec.textFrame.wordWrap;
              if (spec.textFrame.leftMargin !== undefined) tf.leftMargin = spec.textFrame.leftMargin;
              if (spec.textFrame.rightMargin !== undefined) tf.rightMargin = spec.textFrame.rightMargin;
              if (spec.textFrame.topMargin !== undefined) tf.topMargin = spec.textFrame.topMargin;
              if (spec.textFrame.bottomMargin !== undefined) {
                tf.bottomMargin = spec.textFrame.bottomMargin;
              }
            }
          }
          if (spec.name !== undefined) shape.name = spec.name;
          shape.load("id");
          proxies.push(shape);
        }

        await context.sync();
        for (const p of proxies) {
          ids.push(p.id);
          // Per MS perf guidance, release the proxy after we've read its id
          // so the host doesn't keep tracking it across subsequent chunks.
          context.trackedObjects.remove(p);
        }
      }

      return { slideIndex: idx, ids };
    }
    case "addPicture": {
      const idx = resolveSlideIndex(op.slide, prev);
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const slide = slides.items[idx];
      if (!slide) throw new Error(`No slide at index ${idx}`);
      // Prefer preview `addPicture` when present; otherwise GA 1.8
      // ShapeFill.setImage on a rectangle (no border).
      type AddPictureFn = (
        base64: string,
        options: { left: number; top: number; width: number; height: number },
      ) => PowerPoint.Shape;
      const shapesWithAddPicture = slide.shapes as unknown as { addPicture?: AddPictureFn };
      const addPicture = shapesWithAddPicture.addPicture;
      let shape: PowerPoint.Shape;
      if (typeof addPicture === "function") {
        shape = addPicture.call(slide.shapes, op.base64, op.rect);
      } else if (
        typeof Office !== "undefined" &&
        !!Office.context?.requirements?.isSetSupported("PowerPointApi", "1.8")
      ) {
        shape = slide.shapes.addGeometricShape(
          "Rectangle" as PowerPoint.GeometricShapeType,
          op.rect,
        );
        shape.fill.setImage(op.base64);
        shape.lineFormat.transparency = 1;
      } else {
        throw new Error(
          "Picture insertion not supported in this PowerPoint version (needs addPicture preview or PowerPointApi 1.8 ShapeFill.setImage)",
        );
      }
      if (op.name !== undefined) shape.name = op.name;
      shape.load("id");
      await context.sync();
      const shapeId = shape.id;
      context.trackedObjects.remove(shape);
      return { slideIndex: idx, shapeId };
    }
    case "getSlideImage": {
      if (
        typeof Office === "undefined" ||
        !Office.context?.requirements?.isSetSupported("PowerPointApi", "1.8")
      ) {
        throw new Error(
          "Slide image capture requires PowerPointApi 1.8 (Slide.getImageAsBase64)",
        );
      }
      const idx = resolveSlideIndex(op.slide, prev);
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const slide = slides.items[idx];
      if (!slide) throw new Error(`No slide at index ${idx}`);
      const options: PowerPoint.SlideGetImageOptions = {};
      if (op.width !== undefined) options.width = op.width;
      if (op.height !== undefined) options.height = op.height;
      const result = slide.getImageAsBase64(options);
      await context.sync();
      return { base64: result.value, mediaType: "image/png", slideIndex: idx };
    }
    case "oj.get": {
      const target = walkPath(context.presentation, op.path, prev);
      if (isClientResult(target)) {
        await context.sync();
        return target.value;
      }
      if (hasLoad(target)) {
        target.load(op.load ?? "");
        await context.sync();
        if (hasToJSON(target)) return target.toJSON();
        return target;
      }
      // Scalar / plain value already loaded.
      return target;
    }
    case "oj.call": {
      const result = walkPath(context.presentation, op.path, prev);
      if (isClientResult(result)) {
        await context.sync();
        return result.value;
      }
      if (hasLoad(result)) {
        result.load(op.load ?? "");
        await context.sync();
        if (hasToJSON(result)) return result.toJSON();
        return null;
      }
      // Void method (e.g. setSolidColor, delete): just queue. The next op
      // that needs a sync — or the implicit sync at the end of PowerPoint.run
      // — will flush the queued call. Per MS perf guidance, "minimize the
      // number of sync() calls" — syncing per op was the prior hot path.
      return null;
    }
    case "oj.set": {
      if (op.path.length === 0) {
        throw new Error("oj.set requires at least one path segment");
      }
      const last = op.path[op.path.length - 1];
      if (typeof last !== "string") {
        throw new Error("oj.set: last path segment must be a property name");
      }
      const parent = walkPath(context.presentation, op.path.slice(0, -1), prev);
      if (!isRecord(parent)) {
        throw new Error("oj.set: parent of last segment is not an object");
      }
      parent[last] = resolveDeep(op.value, prev);
      // No sync — queued writes flush on the next sync that's actually
      // needed, or at the end of PowerPoint.run.
      return { ok: true };
    }
    case "deleteSlidesByIds": {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const idSet = new Set(op.ids);
      const toDelete: PowerPoint.Slide[] = [];
      for (const s of slides.items) {
        if (idSet.has(s.id)) toDelete.push(s);
      }
      for (const s of toDelete) s.delete();
      if (toDelete.length > 0) await context.sync();
      return { deleted: toDelete.length };
    }
    case "deleteScratchSlides": {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const marker = `[${op.scratchTag}]`;

      const all: { slide: PowerPoint.Slide }[] = [];
      for (let i = 0; i < slides.items.length; i++) {
        const slide = slides.items[i];
        if (!slide) continue;
        slide.shapes.load("items/type");
        all.push({ slide });
      }
      if (all.length > 0) await context.sync();

      const probes: { slide: PowerPoint.Slide; titleShape: PowerPoint.Shape }[] = [];
      for (const a of all) {
        const titleShape = a.slide.shapes.items[0];
        if (!titleShape) continue;
        if (!TEXT_BEARING_TYPES.has(titleShape.type)) continue;
        titleShape.textFrame.textRange.load("text");
        probes.push({ slide: a.slide, titleShape });
      }
      if (probes.length > 0) await context.sync();

      const toDelete: PowerPoint.Slide[] = [];
      for (const p of probes) {
        const text = p.titleShape.textFrame.textRange.text ?? "";
        if (text.startsWith(marker)) toDelete.push(p.slide);
      }
      for (const slide of toDelete) slide.delete();
      if (toDelete.length > 0) await context.sync();
      return { deleted: toDelete.length };
    }
  }
};
